import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import Swal from 'sweetalert2';
import { useAuth } from '../../contexts/AuthContext';
import ApplicationTabPanel from '../../components/externalLoans/ApplicationTabPanel';
import ApprovalsTabPanel from '../../components/externalLoans/ApprovalsTabPanel';
import ContractPreview from '../../components/externalLoans/ContractPreview';
import BellicksReviewTabPanel from '../../components/externalLoans/BellicksReviewTabPanel';
import RepaymentTabPanel from '../../components/externalLoans/RepaymentTabPanel';
import WorkflowTimeline from '../../components/externalLoans/WorkflowTimeline';
import { externalLoanButtonSx, externalLoanCardSx, externalLoanInteractiveCardSx } from '../../components/externalLoans/externalLoanCardStyles';
import { EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE } from '../../constants/externalLoanConstants';
import {
  applyExternalLoanApiLifecycle,
  buildExternalLoanApplicationContext,
  buildMockSubmittedExternalLoanApplication,
  formatExternalLoanTerm,
  getExternalLoanDataSource,
  getMockExternalLoanApplications,
  getSimulationStateConfig,
  isBellicksIntegrationTestRateEnabled,
} from '../../services/externalLoanDataProvider';
import { decideExternalLoanApplication, getExternalLoanApplicationSourceData, getRecentApplications, submitLoan } from '../../services/externalLoanService';
import { buildExternalLoanSubmissionPayload } from '../../hooks/useExternalLoan';
import { formatDateTimeEAT } from '../../shared/formatDateTimeEAT';
import {
  approveExternalLoanApplicationMock,
  canStartNewExternalLoanApplication,
  getAvailableTabsForState,
  getDefaultTabForState,
  getExternalLoanApprovalContext,
  getNextActionGuidance,
  normalizeExternalLoanRole,
  getPrimaryExternalLoanAction,
  rejectExternalLoanApplicationMock,
} from '../../services/externalLoanWorkspaceState';

const brand = '#800b33';
const brandHover = '#6c0a2b';
const currencyFmt = (value) => new Intl.NumberFormat('en-TZ', { maximumFractionDigits: 0 }).format(Number(value || 0));

const normalizeStatus = (status) => String(status || 'pending').trim().toLowerCase().replace(/\s+/g, '-');

const statusChipProps = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized.includes('rejected') || normalized === 'defaulted') return { label: status, color: 'error' };
  if (normalized.includes('approved') || normalized.includes('disbursed') || normalized.includes('repaid')) return { label: status, color: 'success' };
  if (normalized.includes('review') || normalized.includes('submitted')) return { label: status, color: 'info' };
  if (normalized.includes('overdue') || normalized.includes('pending') || normalized.includes('awaiting')) return { label: status, color: 'warning' };
  return { label: status || 'Pending', color: 'default' };
};

const DetailMetric = ({ label, value, helper }) => (
  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={900}>
      {value}
    </Typography>
    {helper ? (
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    ) : null}
  </Card>
);

const detailSectionLabels = {
  application: 'Application',
  approvals: 'Approvals',
  contract: 'Contract',
  'bellicks-review': 'Bellicks Review',
  repayment: 'Repayment',
  timeline: 'Timeline',
};

const mockRoleOptions = ['Normal Member', 'Chairperson', 'Secretary', 'Accountant'];

const timelinePhaseByState = {
  eligible: 'submitted',
  draft: 'submitted',
  'validation-failed': 'review',
  submitted: 'review',
  'awaiting-chairperson': 'chairperson',
  'awaiting-secretary': 'secretary',
  'awaiting-accountant': 'accountant',
  'provider-sync-pending': 'bellicks',
  'provider-sync-failed': 'bellicks',
  'bellicks-submitted': 'bellicks',
  'gm-review': 'gm',
  approved: 'approved',
  rejected: 'approved',
  disbursed: 'disbursed',
  'partially-repaid': 'repayment',
  'fully-repaid': 'completed',
  overdue: 'repayment',
  defaulted: 'repayment',
  cancelled: 'completed',
};

const ExternalLoansPage = () => {
  const { user } = useAuth();
  const externalLoanDataSource = getExternalLoanDataSource({ env: process.env });
  const integrationTestRateEnabled = isBellicksIntegrationTestRateEnabled({
    dataSource: externalLoanDataSource,
    env: process.env,
  });
  const isApiMode = externalLoanDataSource === 'api';
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState('contract');
  const [isApplicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [mockRole, setMockRole] = useState('Secretary');
  const [selectedApplicationId, setSelectedApplicationId] = useState(() => (
    isApiMode ? null : 'mock-eligible'
  ));
  const [applications, setApplications] = useState(() => (
    isApiMode ? [] : getMockExternalLoanApplications()
  ));
  const [portfolioState, setPortfolioState] = useState({
    loading: isApiMode,
    error: null,
    loaded: !isApiMode,
  });
  const [applicationSourceState, setApplicationSourceState] = useState({
    loading: false,
    error: null,
    context: null,
    loaded: false,
  });

  const memberSummary = useMemo(
    () => ({
      currentSavings: user?.current_savings ?? user?.member?.current_savings ?? 1_000_000,
      protectedSavingsPercentage: user?.protected_savings_percentage ?? EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE,
    }),
    [user],
  );

  const filteredApplications = useMemo(() => {
    const query = search.trim().toLowerCase();
    return applications.filter((application) => {
      const matchesSearch = !query || [
        application.referenceNumber,
        application.applicant,
        application.status,
        application.currentStage,
      ].some((value) => String(value || '').toLowerCase().includes(query));
      const matchesStatus = statusFilter === 'ALL' || application.statusGroup === normalizeStatus(statusFilter);
      return matchesSearch && matchesStatus;
    });
  }, [applications, search, statusFilter]);

  const selectedApplication = applications.find((application) => application.id === selectedApplicationId)
    || filteredApplications[0]
    || applications[0]
    || null;
  const selectedStateKey = selectedApplication?.stateKey || 'eligible';
  const selectedSnapshot = selectedApplication;
  const selectedStateConfig = selectedApplication?.stateConfig || getSimulationStateConfig('eligible');
  const primaryAction = getPrimaryExternalLoanAction(selectedStateKey);
  const currentApprovalRole = externalLoanDataSource === 'api'
    ? normalizeExternalLoanRole(
      applicationSourceState.context?.activeMembership?.role
      || user?.active_membership?.role
      || user?.member?.role
      || user?.role,
    )
    : mockRole;
  const approvalContext = useMemo(
    () => getExternalLoanApprovalContext(selectedApplication || {}, currentApprovalRole),
    [currentApprovalRole, selectedApplication],
  );
  const showHeaderApplicationAction = canStartNewExternalLoanApplication(selectedStateKey, selectedApplication);
  const showLifecycleStatusAction = Boolean(selectedApplication)
    && primaryAction.label !== 'Apply Now'
    && !approvalContext.canApprove
    && !approvalContext.canReject;
  const nextActionGuidance = getNextActionGuidance(selectedStateKey, selectedSnapshot || {});
  const availableTabs = getAvailableTabsForState(selectedStateKey).filter((tab) => !tab.disabled);
  const activeExternalLoans = applications.filter((application) => ['approved', 'active'].includes(application.statusGroup)).length;
  const totalOutstanding = applications.reduce((sum, application) => sum + Number(application.outstandingAmount || 0), 0);
  const maxEligibleAmount = Math.max(
    0,
    ...applications.map((application) => Number(application.eligibility?.eligibleAmount || 0)),
  );

  const loadApplicationSourceData = useCallback(async () => {
    setApplicationSourceState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const sourceData = await getExternalLoanApplicationSourceData(user || {}, {
        includeProviderInterestRates: integrationTestRateEnabled,
      });
      const context = buildExternalLoanApplicationContext({
        authUser: user || {},
        ...sourceData,
        integrationMode: integrationTestRateEnabled,
      });
      setApplicationSourceState({
        loading: false,
        error: null,
        context,
        loaded: true,
      });
    } catch (error) {
      setApplicationSourceState((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || 'Unable to retrieve your current member application data. Please retry.',
        loaded: true,
      }));
    }
  }, [integrationTestRateEnabled, user]);

  useEffect(() => {
    if (filteredApplications.length > 0 && !filteredApplications.some((application) => application.id === selectedApplicationId)) {
      setSelectedApplicationId(filteredApplications[0].id);
    }
  }, [filteredApplications, selectedApplicationId]);

  useEffect(() => {
    setActiveTab(getDefaultTabForState(selectedStateKey));
  }, [selectedStateKey]);

  const loadApiApplications = useCallback(async () => {
    if (!isApiMode) return;

    setPortfolioState({ loading: true, error: null, loaded: false });
    try {
      const records = await getRecentApplications();
      const mapped = records.map((record) => applyExternalLoanApiLifecycle({}, record));
      setApplications(mapped);
      setSelectedApplicationId(mapped[0]?.id || null);
      setPortfolioState({ loading: false, error: null, loaded: true });
    } catch (error) {
      setApplications([]);
      setSelectedApplicationId(null);
      setPortfolioState({
        loading: false,
        error: error?.message || 'Unable to load external loan applications.',
        loaded: true,
      });
    }
  }, [isApiMode]);

  useEffect(() => {
    if (!isApiMode) return;
    loadApiApplications();
  }, [isApiMode, loadApiApplications]);

  useEffect(() => {
    if (!isApplicationDialogOpen || applicationSourceState.loaded || applicationSourceState.loading) return;
    loadApplicationSourceData();
  }, [applicationSourceState.loaded, applicationSourceState.loading, isApplicationDialogOpen, loadApplicationSourceData]);

  const kpiCards = [
    {
      label: 'Highest Eligible Amount',
      value: `${currencyFmt(maxEligibleAmount)} TZS`,
      icon: <TimelineIcon sx={{ color: brand }} />,
    },
    {
      label: 'Active External Loans',
      value: activeExternalLoans,
      icon: <PaidOutlinedIcon sx={{ color: brand }} />,
    },
    {
      label: 'Portfolio Outstanding',
      value: `${currencyFmt(totalOutstanding)} TZS`,
      icon: <AccountBalanceIcon sx={{ color: brand }} />,
    },
  ];

  const handlePrimaryAction = () => {
    if (primaryAction.label === 'Apply Now') {
      handleOpenApplicationDialog();
      return;
    }
    setActiveTab(primaryAction.targetTab || getDefaultTabForState(selectedStateKey));
  };

  const handleOpenApplicationDialog = () => {
    setApplicationDialogOpen(true);
  };

  const handleCloseApplicationDialog = () => {
    setApplicationDialogOpen(false);
  };

  const handleApplicationSubmit = async (draft) => {
    let submittedApplication;
    let message;

    if (isApiMode) {
      try {
        const savvyyResponse = await submitLoan(buildExternalLoanSubmissionPayload(draft));
        submittedApplication = applyExternalLoanApiLifecycle({}, savvyyResponse);
        message = `${submittedApplication.referenceNumber} submitted to Savvyy for internal approval.`;
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || 'Unable to submit the external loan application.',
          severity: 'error',
        });
        throw error;
      }
    } else {
      submittedApplication = buildMockSubmittedExternalLoanApplication(draft, {
        currentSavings: applicationSourceState.context?.eligibility?.currentSavings || memberSummary.currentSavings,
      });
      message = `${submittedApplication.referenceNumber} submitted locally for review. No live API request was made.`;
    }

    setApplications((prev) => [submittedApplication, ...prev]);
    setSelectedApplicationId(submittedApplication.id);
    setStatusFilter('ALL');
    setApplicationDialogOpen(false);
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const replaceSelectedApplication = (updatedApplication) => {
    const applicationWithState = {
      ...updatedApplication,
      stateConfig: getSimulationStateConfig(updatedApplication.stateKey),
      statusGroup: updatedApplication.statusGroup || 'pending',
    };
    setApplications((prev) => prev.map((application) => (
      application.id === applicationWithState.id ? applicationWithState : application
    )));
    setSelectedApplicationId(applicationWithState.id);
    return applicationWithState;
  };

  const handleApproveExternalLoan = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Approve External Loan Application',
      html: `
        <div style="text-align:left">
          <p><strong>Application:</strong> ${selectedApplication.referenceNumber}</p>
          <p><strong>Applicant:</strong> ${selectedApplication.applicant}</p>
          <p><strong>Requested amount:</strong> TZS ${currencyFmt(selectedApplication.amount)}</p>
          <p><strong>Approver role:</strong> ${approvalContext.currentUserRole}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: brand,
      cancelButtonColor: '#9e9e9e',
    });

    if (!result.isConfirmed) return;

    if (externalLoanDataSource === 'api' && String(selectedApplication.id).startsWith('api-')) {
      try {
        const response = await decideExternalLoanApplication(
          selectedApplication.referenceNumber,
          'APPROVE',
        );
        const updatedApplication = replaceSelectedApplication(
          applyExternalLoanApiLifecycle(selectedApplication, response),
        );
        setActiveTab(getDefaultTabForState(updatedApplication.stateKey));
        setSnackbar({
          open: true,
          message: response.provider_sync_error
            ? `${selectedApplication.referenceNumber} approved. Automatic Bellicks sync needs administrator attention.`
            : `${selectedApplication.referenceNumber} approved successfully.`,
          severity: response.provider_sync_error ? 'warning' : 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || 'Unable to approve the external loan application.',
          severity: 'error',
        });
      }
      return;
    }

    const updatedApplication = replaceSelectedApplication(
      approveExternalLoanApplicationMock(selectedApplication, approvalContext.currentUserRole),
    );
    setActiveTab(getDefaultTabForState(updatedApplication.stateKey));
    setSnackbar({
      open: true,
      message: `${selectedApplication.referenceNumber} approved locally as ${approvalContext.currentUserRole}.`,
      severity: 'success',
    });
  };

  const handleRejectExternalLoan = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Reject External Loan Application',
      html: `
        <div style="text-align:left">
          <p><strong>Application:</strong> ${selectedApplication.referenceNumber}</p>
          <p><strong>Applicant:</strong> ${selectedApplication.applicant}</p>
          <p><strong>Requested amount:</strong> TZS ${currencyFmt(selectedApplication.amount)}</p>
          <p><strong>Approver role:</strong> ${approvalContext.currentUserRole}</p>
        </div>
      `,
      input: 'textarea',
      inputLabel: 'Rejection reason',
      inputPlaceholder: 'Enter the reason for rejecting this application',
      inputValidator: (value) => (!String(value || '').trim() ? 'Rejection reason is required.' : null),
      showCancelButton: true,
      confirmButtonText: 'Reject Application',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#9e9e9e',
    });

    if (!result.isConfirmed) return;

    if (externalLoanDataSource === 'api' && String(selectedApplication.id).startsWith('api-')) {
      try {
        const response = await decideExternalLoanApplication(
          selectedApplication.referenceNumber,
          'REJECT',
          result.value.trim(),
        );
        replaceSelectedApplication(
          applyExternalLoanApiLifecycle(selectedApplication, response),
        );
        setActiveTab('approvals');
        setSnackbar({
          open: true,
          message: `${selectedApplication.referenceNumber} rejected.`,
          severity: 'warning',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || 'Unable to reject the external loan application.',
          severity: 'error',
        });
      }
      return;
    }

    replaceSelectedApplication(
      rejectExternalLoanApplicationMock(selectedApplication, approvalContext.currentUserRole, result.value.trim()),
    );
    setActiveTab('approvals');
    setSnackbar({
      open: true,
      message: `${selectedApplication.referenceNumber} rejected locally as ${approvalContext.currentUserRole}.`,
      severity: 'warning',
    });
  };

  const renderSelectedApplicationDetails = () => (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, ...externalLoanCardSx }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={800}>Application Details</Typography>
          <Typography variant="body2" color="text.secondary">
            Selected application data for {selectedSnapshot.referenceNumber}.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Applicant" value={selectedSnapshot.memberProfile.memberName} helper={selectedSnapshot.memberProfile.phoneNumber} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Group" value={selectedSnapshot.groupProfile.name} helper={selectedSnapshot.groupProfile.district} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric
              label="Requested Amount"
              value={`${currencyFmt(selectedSnapshot.loanSummary.requestedAmount)} TZS`}
              helper={`Term: ${formatExternalLoanTerm(selectedSnapshot.loanSummary)}`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Eligible Amount" value={`${currencyFmt(selectedSnapshot.eligibility.eligibleAmount)} TZS`} helper="After protected savings" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Total Interest" value={`${currencyFmt(selectedSnapshot.loanSummary.interest)} TZS`} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Total Payable" value={`${currencyFmt(selectedSnapshot.loanSummary.totalPayable)} TZS`} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Collateral" value={selectedSnapshot.collateral.type} helper={selectedSnapshot.collateral.description} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailMetric label="Last Updated" value={formatDateTimeEAT(selectedSnapshot.lastUpdated)} helper={selectedSnapshot.currentStage} />
          </Grid>
        </Grid>
        {selectedSnapshot.loanSummary.rejectionReason ? (
          <Alert severity="error">{selectedSnapshot.loanSummary.rejectionReason}</Alert>
        ) : null}
        {selectedSnapshot.loanSummary.cancelledReason ? (
          <Alert severity="warning">{selectedSnapshot.loanSummary.cancelledReason}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );

  const renderDetailPanel = () => {
    switch (activeTab) {
      case 'application':
        return renderSelectedApplicationDetails();
      case 'approvals':
        return <ApprovalsTabPanel guarantors={approvalContext.guarantors} activeApproverRole={approvalContext.activeApproverRole} />;
      case 'contract':
        return (
          <ContractPreview
            contract={selectedSnapshot.contract}
            memberProfile={selectedSnapshot.memberProfile}
            groupProfile={selectedSnapshot.groupProfile}
            loanSummary={selectedSnapshot.loanSummary}
            guarantors={selectedSnapshot.guarantors}
          />
        );
      case 'bellicks-review':
        return <BellicksReviewTabPanel review={selectedSnapshot.bellicksReview} />;
      case 'repayment':
        return <RepaymentTabPanel repayment={selectedSnapshot.repayment} />;
      case 'timeline':
        return <WorkflowTimeline currentPhase={timelinePhaseByState[selectedStateKey] || 'submitted'} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            External Loans
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Manage Bellicks Finance external loan applications, approvals, contracts, disbursements and repayments.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size="small"
            onChange={(_, nextValue) => {
              if (nextValue) setStatusFilter(nextValue);
            }}
          >
            <ToggleButton value="ALL">All Status</ToggleButton>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="review">Review</ToggleButton>
            <ToggleButton value="approved">Approved</ToggleButton>
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="overdue">Overdue</ToggleButton>
            <ToggleButton value="completed">Completed</ToggleButton>
            <ToggleButton value="rejected">Rejected</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            sx={{ minWidth: 230 }}
            placeholder="Search application or member..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {showHeaderApplicationAction ? (
            <Button
              variant="contained"
              startIcon={<SavingsOutlinedIcon />}
              onClick={handleOpenApplicationDialog}
              sx={{ ...externalLoanButtonSx, bgcolor: brand, '&:hover': { bgcolor: brandHover } }}
            >
              Apply Now
            </Button>
          ) : null}
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} md={4} key={card.label}>
            <Card sx={{ p: 2, ...externalLoanInteractiveCardSx }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {card.icon}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {card.value}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card sx={{ ...externalLoanCardSx, mb: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    p: 2,
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h6" fontWeight={800}>
                    External Loan Applications
                  </Typography>
                  <Tooltip title="Status is based on application, guarantor, Bellicks, disbursement and repayment progress.">
                    <InfoOutlinedIcon sx={{ color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Divider />
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Application</TableCell>
                        <TableCell>Applicant</TableCell>
                        <TableCell align="right">Requested</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portfolioState.loading ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                              <CircularProgress size={20} />
                              <Typography color="text.secondary">Loading external loan applications…</Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {!portfolioState.loading && portfolioState.error ? (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ py: 2 }}>
                            <Alert
                              severity="error"
                              action={<Button color="inherit" size="small" onClick={loadApiApplications}>Retry</Button>}
                            >
                              {portfolioState.error}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {!portfolioState.loading && !portfolioState.error && filteredApplications.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            {isApiMode
                              ? 'No persisted external loan applications found.'
                              : 'No external loan applications found.'}
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {!portfolioState.loading && !portfolioState.error
                        ? filteredApplications.map((application) => {
                          const chip = statusChipProps(application.status);
                          return (
                            <TableRow
                              key={application.id}
                              hover
                              selected={application.id === selectedApplication?.id}
                              onClick={() => setSelectedApplicationId(application.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={700}>
                                  {application.referenceNumber}
                                </Typography>
                                <Chip size="small" label={chip.label} color={chip.color} sx={{ mt: 0.5 }} />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <PersonOutlineOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                  <Typography variant="body2">{application.applicant}</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {application.currentStage}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={700} color="text.primary">
                                  {currencyFmt(application.amount)} TZS
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Updated {formatDateTimeEAT(application.lastUpdated)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                        : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            {selectedApplication ? (
              <Card sx={externalLoanCardSx}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={900}>
                      Application {selectedApplication.referenceNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Borrower: <b>{selectedApplication.applicant}</b>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Stage: {selectedApplication.currentStage} • Updated: {formatDateTimeEAT(selectedApplication.lastUpdated)}
                    </Typography>
                  </Box>
                  <Stack spacing={1} alignItems="flex-end">
                    <Chip {...statusChipProps(selectedApplication.status)} size="small" />
                    <Chip
                      icon={<MonetizationOnOutlinedIcon />}
                      size="small"
                      label={`${currencyFmt(selectedApplication.outstandingAmount)} TZS outstanding`}
                      variant="outlined"
                    />
                  </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Requested Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedApplication.amount)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Term: {formatExternalLoanTerm(selectedSnapshot.loanSummary)}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Total Interest
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedSnapshot.loanSummary.interest)} TZS
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Total Payable
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedSnapshot.loanSummary.totalPayable)} TZS
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Eligible Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedSnapshot.eligibility.eligibleAmount)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Protected savings preserved
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Repayment Exposure
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedSnapshot.loanSummary.outstandingBalance)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Status: {selectedStateConfig.badge}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Current status</Typography>
                        <Typography variant="body1" fontWeight={800}>
                          {approvalContext.isAwaitingGuarantors || approvalContext.rejectedGuarantor
                            ? approvalContext.title
                            : selectedStateConfig.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {approvalContext.isAwaitingGuarantors || approvalContext.rejectedGuarantor
                            ? approvalContext.message
                            : nextActionGuidance}
                        </Typography>
                      </Box>
                      {approvalContext.canApprove || approvalContext.canReject ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRejectExternalLoan}
                            sx={{ borderRadius: 1.5, fontWeight: 800 }}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<AssignmentTurnedInOutlinedIcon />}
                            onClick={handleApproveExternalLoan}
                            sx={{ ...externalLoanButtonSx, bgcolor: brand, '&:hover': { bgcolor: brandHover } }}
                          >
                            Approve
                          </Button>
                        </Stack>
                      ) : null}
                      {showLifecycleStatusAction ? (
                        <Button
                          variant="contained"
                          startIcon={<AssignmentTurnedInOutlinedIcon />}
                          onClick={handlePrimaryAction}
                          sx={{ ...externalLoanButtonSx, bgcolor: brand, '&:hover': { bgcolor: brandHover } }}
                        >
                          {primaryAction.label}
                        </Button>
                      ) : null}
                    </Stack>
                    {externalLoanDataSource === 'mock'
                    && (approvalContext.isAwaitingGuarantors || approvalContext.rejectedGuarantor) ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>Mock approval role preview</Typography>
                        <ToggleButtonGroup
                          value={mockRole}
                          exclusive
                          size="small"
                          onChange={(_, nextRole) => {
                            if (nextRole) setMockRole(nextRole);
                          }}
                          sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {mockRoleOptions.map((role) => (
                            <ToggleButton key={role} value={role}>
                              {role}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    ) : null}
                  </Stack>
                </Card>

                <ToggleButtonGroup
                  value={activeTab}
                  exclusive
                  size="small"
                  onChange={(_, nextValue) => {
                    if (nextValue) setActiveTab(nextValue);
                  }}
                  sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}
                >
                  {availableTabs.map((tab) => (
                    <ToggleButton key={tab.key} value={tab.key}>
                      {detailSectionLabels[tab.key] || tab.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {renderDetailPanel()}
              </CardContent>
              </Card>
            ) : (
              <Card sx={externalLoanCardSx}>
                <CardContent>
                  <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 320, textAlign: 'center' }}>
                    {portfolioState.loading ? (
                      <>
                        <CircularProgress />
                        <Typography color="text.secondary">Loading external loan application details…</Typography>
                      </>
                    ) : portfolioState.error ? (
                      <Alert
                        severity="error"
                        action={<Button color="inherit" size="small" onClick={loadApiApplications}>Retry</Button>}
                        sx={{ width: '100%' }}
                      >
                        {portfolioState.error}
                      </Alert>
                    ) : (
                      <>
                        <AccountBalanceIcon color="action" sx={{ fontSize: 48 }} />
                        <Typography variant="h6" fontWeight={800}>No persisted external loan applications yet</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                          Applications submitted through Savvyy will appear here with their internal approval and provider status.
                        </Typography>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
      </Grid>

      <Dialog
        open={isApplicationDialogOpen}
        onClose={handleCloseApplicationDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ color: brand, fontWeight: 900 }}>
          Apply for External Loan
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
          <ApplicationTabPanel
            applicationContext={applicationSourceState.context}
            loading={applicationSourceState.loading}
            error={applicationSourceState.error}
            onRetry={loadApplicationSourceData}
            onSubmit={handleApplicationSubmit}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApplicationDialog}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExternalLoansPage;
